import * as Yup from "yup";
import { regexList } from "../../../Constants";
import { subIndustryOthers } from "../../../store/eligibility/util";
import { CompanyDetailsState } from "../../../store/eligibility/state";
import { MultiTags } from "../../../components/formHelpers/FormMultiInputTags";

export const CompanyDetailsFoldConstants = {
  labels: {
    unitAddress: "Primary Address of the Unit",
    unitAddressType: "Property Type",
    companyType: "Type of company",
    industryType: "Industry",
    subIndustryType: "Sub-Industry",
    cinNumber: "CIN number",
    uanNumber: "UAN number",
    employeesCount: "Number of Employees",
    keyCustomers: "Key Customers",
    keySuppliers: "Key Suppliers",
    others: subIndustryOthers,
    state: "State",
    city: "City",
    pincode: "PIN Code",
    natureOfBusiness: "Nature of business",
    officeStability: "Office stability (in months)",
    officialEmail: "Official email",
    reference1Name: "Reference_1 name",
    reference1Contact: "Reference_1 contact",
    reference2Name: "Reference_2 name",
    reference2Contact: "Reference_2 contact",
    dateOfIncorporation: "Date of Incorporation",
    dateFormat: "DD-MM-YYYY"
  },
  placeholders: {
    industryType: "Search by Industry",
    subIndustryType: "Search by Sub-Industry",
    subIndustryManual: "Enter a suitable Sub-Industry",
    state: "State",
    city: "Search by city",
    pincode: "PIN Code",
    natureOfBusiness: "Nature of business",
    officeStability: "Office statbility in months",
    officialEmail: "Official email",
    reference1Name: "Reference_1 name",
    reference1Contact: "Reference_1 contact",
    reference2Name: "Reference_2 name",
    reference2Contact: "Reference_2 contact"
  },
  buttons: {
    next: "Next"
  }
};

export enum CompanyType {
  "Proprietorship" = "proprietorship",
  "Partnership" = "partnership",
  "Private Limited" = "private_limited"
}

const errorMessages = {
  required: "Required",
  duplicate: "GSTIN already exists",
  length: (length: number) => `Must be ${length} characters`,
  min: (length: number) => `Must be at greater than ${length}`,
  invalid: "Invalid format",
  invalidUanNo: "Must be number with 12 characters",
  gstinLoading: "GSTIN is being authenticated",
  userGstin: "Can't be your own GSTIN",
  invalidPhoneNumber: "Enter a valid Phone number",
  invalidEmail: "Enter a valid email"
};

export const CompanyDetailsFoldValidationSchema = (gstin: string) =>
  Yup.object<CompanyDetailsState>().shape<CompanyDetailsState>({
    unitAddress: Yup.string()
      .required(errorMessages.required)
      .matches(regexList.minimum1Character, errorMessages.invalid)
      .nullable(),
    natureOfBusiness: Yup.string()
      .required(errorMessages.required)
      .matches(regexList.minimum1Character, errorMessages.invalid)
      .nullable(),
    reference1Name: Yup.string()
      .required(errorMessages.required)
      .matches(regexList.minimum1Character, errorMessages.invalid)
      .nullable(),
    reference2Name: Yup.string()
      .required(errorMessages.required)
      .matches(regexList.minimum1Character, errorMessages.invalid)
      .nullable(),
    reference1Contact: Yup.string()
      .matches(regexList.phoneNumber, errorMessages.invalidPhoneNumber)
      .required(errorMessages.required)
      .nullable(),
    reference2Contact: Yup.string()
      .matches(regexList.phoneNumber, errorMessages.invalidPhoneNumber)
      .required(errorMessages.required)
      .nullable(),
    officialEmail: Yup.string()
      .email(errorMessages.invalidEmail)
      .required(errorMessages.required)
      .nullable(),
    officeStability: Yup.number()
      .required(errorMessages.required)
      .nullable(),
    unitAddressType: Yup.string()
      .required(errorMessages.required)
      .nullable(),
    industryType: Yup.string()
      .required(errorMessages.required)
      .nullable(),
    state: Yup.string()
      .required(errorMessages.required)
      .nullable(),
    pincode: Yup.string()
      .required(errorMessages.required)
      .nullable(),
    subIndustryType: Yup.string()
      .required(errorMessages.required)
      .nullable(),
    subIndustryManual: Yup.string().when("subIndustryType", {
      is: CompanyDetailsFoldConstants.labels.others,
      then: Yup.string()
        .required(errorMessages.required)
        .matches(regexList.minimum1Character, errorMessages.invalid)
    }),
    companyType: Yup.string()
      .nullable()
      .required(errorMessages.required),
    cinNumber: Yup.string()
      .nullable()
      .when("companyType", {
        is: CompanyType["Private Limited"],
        then: Yup.string()
          .required(errorMessages.required)
          .matches(regexList.cinNumber, errorMessages.invalid)
      }),
    uanNumber: Yup.string()
      .nullable()
      .when("companyType", {
        is: data =>
          data === CompanyType.Partnership ||
          data === CompanyType.Proprietorship,
        then: Yup.string()
          .required(errorMessages.required)
          .matches(regexList.uanNumber, errorMessages.invalidUanNo)
      }),
    dateOfIncorporation: Yup.string()
      .required(errorMessages.required)
      .nullable(),
    employeesCount: Yup.number()
      .nullable()
      .typeError(errorMessages.invalid)
      .integer(errorMessages.invalid)
      .min(0, errorMessages.min(0))
      .max(100000000, errorMessages.invalid), //max 8 digits
    currentCustomer: Yup.string()
      .uppercase()
      .matches(regexList.gstin, errorMessages.invalid)
      .notOneOf([gstin], errorMessages.userGstin)
      .test({
        name: "Duplicate GSTIN check",
        test(value) {
          return this.parent.keyCustomers
            .filter((item: MultiTags) => !item.removed)
            .find((item: MultiTags) => item.value === value)
            ? this.createError({
                message: errorMessages.duplicate
              })
            : true;
        }
      }),
    currentSupplier: Yup.string()
      .uppercase()
      .matches(regexList.gstin, errorMessages.invalid)
      .notOneOf([gstin], errorMessages.userGstin)
      .test({
        name: "Duplicate GSTIN check",
        test(value) {
          return this.parent.keySuppliers
            .filter((item: MultiTags) => !item.removed)
            .find((item: MultiTags) => item.value === value)
            ? this.createError({
                message: errorMessages.duplicate
              })
            : true;
        }
      }),
    keyCustomers: Yup.array().of(
      Yup.object().shape({
        value: Yup.string().matches(regexList.gstin),
        loading: Yup.boolean().oneOf([false], errorMessages.gstinLoading)
      })
    ) as Yup.Schema<MultiTags[]>,
    keySuppliers: Yup.array<MultiTags>().of(
      Yup.object().shape<Partial<MultiTags>>({
        value: Yup.string().matches(regexList.gstin),
        loading: Yup.boolean().oneOf([false], errorMessages.gstinLoading)
      })
    ) as Yup.Schema<MultiTags[]>,
    isSubIndustryManual: Yup.boolean()
  });
